import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CountryCodePicker from "@/components/base/CountryCodePicker";
import { countryCodes } from "@/mocks/countryCodes";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { initiateRegister } = useAuth();
  const navigate = useNavigate();

  const [countryCode, setCountryCode] = useState(
    countryCodes.find((c) => c.code === "+20" && c.country === "Egypt") ||
      countryCodes[0]
  );
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const newErrors: { name?: string; phone?: string } = {};
    if (!fullName.trim()) {
      newErrors.name = isAr ? "الاسم الكامل مطلوب" : "Full name is required";
    }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) {
      newErrors.phone = isAr ? "يرجى إدخال رقم هاتف صالح" : "Please enter a valid phone number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    // Simulate sending OTP
    setTimeout(() => {
      initiateRegister(fullName.trim(), phone.replace(/\D/g, ""), countryCode.code);
      setLoading(false);
      navigate("/verify-otp", { state: { mode: "register" } });
    }, 800);
  };

  return (
    <div className="min-h-screen flex flex-col" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-20 md:py-28 bg-background-50">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-accent-50 flex items-center justify-center">
              <i className="ri-user-add-line text-2xl text-accent-500"></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {t("auth.create_account_title")}
            </h1>
            <p className="text-sm text-foreground-500">
              {t("auth.create_account_subtitle")}
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8"
          >
            {/* Full name */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-foreground-700 mb-2">
                {t("auth.full_name")}
              </label>
              <div className="relative">
                <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  placeholder={isAr ? "محمد أحمد" : "Ahmed Mohamed"}
                  className="w-full pl-10 pr-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  autoComplete="name"
                />
              </div>
              {errors.name && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Phone input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">
                {t("auth.phone_number")}
              </label>
              <div className="flex gap-2">
                <CountryCodePicker value={countryCode} onChange={setCountryCode} />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  placeholder={isAr ? "1XX XXX XXXX" : "(555) 123-4567"}
                  className="flex-1 px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  autoComplete="tel"
                />
              </div>
              {errors.phone && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {isAr ? "جاري الإرسال..." : "Sending code..."}
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  {t("auth.send_code")}
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-background-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-background-50 text-xs text-foreground-400">
                  {isAr ? "أو" : "or"}
                </span>
              </div>
            </div>

            {/* Login link */}
            <p className="text-center text-sm text-foreground-500">
              {t("auth.has_account_phone")}{" "}
              <Link
                to="/login"
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer"
              >
                {t("auth.sign_in")}
              </Link>
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}